import { useState } from 'react';

import { Badge, Button, StatusDot } from '@/components/atoms';
import {
  useCreateSite,
  useDeleteSite,
  useSites,
  useTestSite,
  useUpdateSite,
} from '@/hooks/useConfig';
import { ApiError } from '@/services/api';
import type { AuthType, WpSite, WpSiteCreate } from '@/types/config';

import styles from './ConfigSites.module.css';

interface SiteFormState {
  slug: string;
  name: string;
  base_url: string;
  rest_path: string;
  auth_type: AuthType;
  auth_user: string;
  auth_secret: string;
  default_status: string;
  default_author: string; // string in form, parsed on submit
  notes: string;
  field_map: Record<string, string>;
  brand_name: string;
  brand_logo_url: string;
  brand_accent: string;
  voz_tom: string;
  voz_custom: string;
  voz_clickbait: string;
}

// B-25 — presets de voz por marca (o motor injeta o bloco correspondente no
// Artigo Final; vazio = voz default). Só estilo — as travas nunca mudam.
const VOZ_TONS: { value: string; label: string }[] = [
  { value: '', label: 'Padrão do motor (sóbrio)' },
  { value: 'popular', label: 'Popular — simples e envolvente (estilo G1 popular)' },
  { value: 'sobrio', label: 'Sóbrio — analítico (Folha/Estadão)' },
  { value: 'tecnico', label: 'Técnico — especializado, com profundidade' },
  { value: 'jovem', label: 'Jovem — leve e dinâmico (tom de redes)' },
];

const VOZ_CLICKBAIT: { value: string; label: string }[] = [
  { value: '', label: 'Médio (default) — equilíbrio SEO × curiosidade' },
  { value: 'baixo', label: 'Baixo — serviço/resposta no título (utilidade)' },
  { value: 'alto', label: 'Alto — máxima curiosidade (sempre ética)' },
];

// Variáveis do lado Wiredesk que podem ser mapeadas para campos custom do WP.
// O cliente preenche a chave de meta do tema dele (precisa estar registrada com
// show_in_rest no WP). Vazio = não envia.
const PUBLISHER_VARS: { key: string; label: string; placeholder: string }[] = [
  { key: 'chapeu', label: 'Chapéu', placeholder: 'ex. ncnews: _mtb_materia_chapeu' },
  { key: 'linha_fina', label: 'Linha fina', placeholder: 'ex. ncnews: _mtb_materia_linhafina' },
  { key: 'creditos_imagem', label: 'Créditos da imagem', placeholder: 'ex. ncnews: _mtb_photo_credit' },
  { key: 'focus_keyword', label: 'Palavra-chave SEO', placeholder: 'ex.: rank_math_focus_keyword' },
  { key: 'meta_description', label: 'Meta description SEO', placeholder: 'ex.: rank_math_description' },
];

const EMPTY_FORM: SiteFormState = {
  slug: '',
  name: '',
  base_url: '',
  rest_path: '/wp-json/wp/v2',
  auth_type: 'app_password',
  auth_user: '',
  auth_secret: '',
  default_status: 'publish',
  default_author: '',
  notes: '',
  field_map: {},
  brand_name: '',
  brand_logo_url: '',
  brand_accent: '',
  voz_tom: '',
  voz_custom: '',
  voz_clickbait: '',
};

// Nome amigável → slug seguro (ASCII, minúsculo, hífen). Sem depender de libs.
function slugify(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

export function ConfigSites() {
  const { data: sites = [], isLoading } = useSites();
  const createMut = useCreateSite();
  const updateMut = useUpdateSite();
  const deleteMut = useDeleteSite();
  const testMut = useTestSite();

  const [editing, setEditing] = useState<WpSite | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<SiteFormState>(EMPTY_FORM);
  const [msg, setMsg] = useState<string | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [testResults, setTestResults] = useState<
    Record<string, { ok: boolean; text: string }>
  >({});

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditing(null);
    setCreating(true);
    setMsg(null);
    setSlugTouched(false);
  }

  function openEdit(site: WpSite) {
    setForm({
      slug: site.slug,
      name: site.name,
      base_url: site.base_url,
      rest_path: site.rest_path,
      auth_type: site.auth_type,
      auth_user: site.auth_user ?? '',
      auth_secret: '',
      default_status: site.default_status,
      default_author: site.default_author?.toString() ?? '',
      notes: site.notes ?? '',
      field_map: { ...(site.field_map ?? {}) },
      brand_name: site.branding?.brand_name ?? '',
      brand_logo_url: site.branding?.logo_url ?? '',
      brand_accent: site.branding?.accent_hex ?? '',
      voz_tom: site.editorial_profile?.tom ?? '',
      voz_custom: site.editorial_profile?.tom_custom ?? '',
      voz_clickbait: site.editorial_profile?.clickbait ?? '',
    });
    setEditing(site);
    setCreating(false);
    setMsg(null);
    setSlugTouched(true); // ao editar, o slug já existe e é fixo
  }

  function closeModal() {
    setCreating(false);
    setEditing(null);
    setMsg(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    const payload: WpSiteCreate = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      base_url: form.base_url.trim(),
      rest_path: form.rest_path.trim() || '/wp-json/wp/v2',
      auth_type: form.auth_type,
      auth_user: form.auth_user.trim() || null,
      default_status: form.default_status,
      default_author: form.default_author
        ? Number(form.default_author)
        : null,
      notes: form.notes.trim() || null,
      field_map: Object.fromEntries(
        Object.entries(form.field_map)
          .map(([k, v]) => [k, (v ?? '').trim()] as const)
          .filter(([, v]) => v),
      ),
      branding: {
        brand_name: form.brand_name.trim(),
        logo_url: form.brand_logo_url.trim(),
        accent_hex: form.brand_accent.trim() || null,
      },
      editorial_profile: {
        tom: form.voz_tom,
        tom_custom: form.voz_custom.trim(),
        clickbait: form.voz_clickbait,
      },
    };
    if (form.auth_secret) {
      payload.auth_secret = form.auth_secret;
    }
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, payload });
        setMsg('Site atualizado.');
      } else {
        await createMut.mutateAsync(payload);
        setMsg('Site criado.');
      }
      closeModal();
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function handleTest(site: WpSite) {
    setTestResults((prev) => ({
      ...prev,
      [site.id]: { ok: false, text: 'A testar…' },
    }));
    try {
      const res = await testMut.mutateAsync(site.id);
      setTestResults((prev) => ({
        ...prev,
        [site.id]: {
          ok: res.ok,
          text: res.elapsed_ms
            ? `${res.message} · ${res.elapsed_ms}ms`
            : res.message,
        },
      }));
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [site.id]: { ok: false, text: `Erro: ${describeError(err)}` },
      }));
    }
  }

  async function handleDelete(site: WpSite) {
    if (
      !window.confirm(
        `Pausar (soft-delete) o site "${site.name}"? Workflows e artigos ligados a ele continuam, ` +
          `mas o site deixa de aparecer nas dropdowns ativas.`,
      )
    ) {
      return;
    }
    try {
      await deleteMut.mutateAsync(site.id);
      setMsg(`Site "${site.name}" pausado.`);
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <h1 className={styles.title}>Sites WordPress</h1>
        <Button onClick={openCreate}>+ Adicionar site</Button>
      </header>

      {msg && <p className={styles.flash}>{msg}</p>}

      {isLoading && <p className={styles.muted}>Carregando sites…</p>}
      {!isLoading && sites.length === 0 && (
        <p className={styles.muted}>
          Nenhum site WordPress cadastrado. Clique em <strong>+ Adicionar site</strong>{' '}
          para configurar o primeiro.
        </p>
      )}

      <ul className={styles.list}>
        {sites.map((site) => {
          const test = testResults[site.id];
          return (
            <li key={site.id} className={styles.card}>
              <div className={styles.cardHead}>
                <span className={styles.cardTitle}>{site.name}</span>
                <Badge tone={site.active ? 'ok' : 'neutral'} size="sm">
                  {site.active ? 'ativo' : 'pausado'}
                </Badge>
                <span className={styles.slug}>/{site.slug}</span>
              </div>

              <div className={styles.row}>
                <span className={styles.label}>URL</span>
                <a
                  href={site.base_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  {site.base_url}
                </a>
              </div>

              <div className={styles.row}>
                <span className={styles.label}>Auth</span>
                <span className={styles.mono}>
                  {site.auth_type} · {site.auth_user ?? '(sem user)'}
                </span>
                {site.has_secret ? (
                  <span className={styles.mono}>
                    {site.auth_secret_masked}
                  </span>
                ) : (
                  <span className={styles.warn}>sem secret</span>
                )}
              </div>

              <div className={styles.row}>
                <span className={styles.label}>Defaults</span>
                <span className={styles.mono}>
                  status={site.default_status} · author=
                  {site.default_author ?? '—'}
                </span>
              </div>

              {test && (
                <div className={styles.testLine}>
                  <StatusDot state={test.ok ? 'ok' : 'crit'} />
                  <span className={test.ok ? styles.ok : styles.crit}>
                    {test.text}
                  </span>
                </div>
              )}

              <div className={styles.actions}>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleTest(site)}
                  loading={testMut.isPending && test?.text === 'A testar…'}
                >
                  Test connection
                </Button>
                <Button size="sm" variant="secondary" onClick={() => openEdit(site)}>
                  Editar
                </Button>
                {site.active && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(site)}
                  >
                    Pausar
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {(creating || editing) && (
        <div className={styles.modalBackdrop} onClick={closeModal}>
          <form
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
          >
            <h2 className={styles.modalTitle}>
              {editing ? `Editar ${editing.name}` : 'Novo site WordPress'}
            </h2>

            <label className={styles.field}>
              <span>Nome amigável *</span>
              <input
                required
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  // slug auto-gerado do nome enquanto o usuário não o editar à mão
                  // (só ao criar; ao editar o slug é fixo).
                  setForm((f) =>
                    !editing && !slugTouched
                      ? { ...f, name, slug: slugify(name) }
                      : { ...f, name },
                  );
                }}
                placeholder="Tecnologia GNC"
              />
            </label>

            <label className={styles.field}>
              <span>URL base *</span>
              <input
                required
                value={form.base_url}
                onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                placeholder="https://exemplo.com"
              />
            </label>

            <label className={styles.field}>
              <span>Usuário (login do WP)</span>
              <input
                value={form.auth_user}
                onChange={(e) => setForm({ ...form, auth_user: e.target.value })}
                placeholder="email@dominio.com"
              />
            </label>

            <label className={styles.field}>
              <span>
                {editing
                  ? 'Application Password (deixar vazio mantém o atual)'
                  : 'Application Password *'}
              </span>
              <input
                type="password"
                value={form.auth_secret}
                onChange={(e) => setForm({ ...form, auth_secret: e.target.value })}
                placeholder={editing ? '••••••••' : 'XXXX XXXX XXXX XXXX'}
                autoComplete="new-password"
              />
            </label>

            <details className={styles.advanced}>
              <summary>Avançado (identificador, autor, autenticação)</summary>

              <label className={styles.field}>
                <span>Slug (identificador — gerado do nome)</span>
                <input
                  required
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setForm({ ...form, slug: e.target.value });
                  }}
                  placeholder="meu-portal"
                  pattern="[a-z0-9_-]+"
                  disabled={Boolean(editing)}
                />
              </label>

              <div className={styles.fieldRow}>
                <label className={styles.field}>
                  <span>Default author (ID WP)</span>
                  <input
                    type="number"
                    min={1}
                    value={form.default_author}
                    onChange={(e) => setForm({ ...form, default_author: e.target.value })}
                  />
                </label>
                <label className={styles.field}>
                  <span>Default status</span>
                  <select
                    value={form.default_status}
                    onChange={(e) => setForm({ ...form, default_status: e.target.value })}
                  >
                    <option value="publish">publish</option>
                    <option value="draft">draft</option>
                    <option value="pending">pending</option>
                    <option value="private">private</option>
                  </select>
                </label>
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.field}>
                  <span>REST path</span>
                  <input
                    value={form.rest_path}
                    onChange={(e) => setForm({ ...form, rest_path: e.target.value })}
                  />
                </label>
                <label className={styles.field}>
                  <span>Auth type</span>
                  <select
                    value={form.auth_type}
                    onChange={(e) => {
                      const auth_type = e.target.value as AuthType;
                      // Cada driver tem seu caminho. Trocar o driver e deixar o
                      // caminho do outro é exatamente o que gerou o 404 de 01/09.
                      let rest_path = form.rest_path;
                      if (auth_type === 'token' && rest_path.trim() === '/wp-json/wp/v2')
                        rest_path = '/api/postages/';
                      else if (auth_type !== 'token' && rest_path.trim() === '/api/postages/')
                        rest_path = '/wp-json/wp/v2';
                      setForm({ ...form, auth_type, rest_path });
                    }}
                  >
                    <option value="app_password">Application Password (WordPress)</option>
                    <option value="jwt">JWT</option>
                    <option value="basic">Basic</option>
                    <option value="token">Token (API CONECTA / Django REST)</option>
                  </select>
                </label>
              </div>
            </details>

            <label className={styles.field}>
              <span>Notas</span>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </label>

            <fieldset className={styles.fieldset}>
              <legend>Marca do destino (capas white-label)</legend>
              <p className={styles.muted}>
                Identidade usada nas capas geradas (fábricas e futuras mídias):
                nome em texto, ou logo (URL) que substitui o texto, e cor de fundo.
              </p>
              <label className={styles.field}>
                <span>Nome da marca no card</span>
                <input
                  value={form.brand_name}
                  placeholder="ex.: MIRAI NEWS (vazio = nome do site)"
                  onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                />
              </label>
              <label className={styles.field}>
                <span>Logo (URL — substitui o texto)</span>
                <input
                  value={form.brand_logo_url}
                  placeholder="https://…/logo.png"
                  onChange={(e) => setForm({ ...form, brand_logo_url: e.target.value })}
                />
              </label>
              <label className={styles.field}>
                <span>Cor de fundo (hex, opcional)</span>
                <input
                  value={form.brand_accent}
                  placeholder="#0a6c4a (vazio = cor da fábrica)"
                  onChange={(e) => setForm({ ...form, brand_accent: e.target.value })}
                />
              </label>
            </fieldset>

            <fieldset className={styles.fieldset}>
              <legend>Perfil editorial (voz da marca)</legend>
              <p className={styles.muted}>
                Como o motor <strong>escreve</strong> para este destino. Muda só o estilo —
                as travas (data, fidelidade à fonte, limite do título) nunca mudam.
              </p>
              <label className={styles.field}>
                <span>Tom de voz</span>
                <select
                  value={form.voz_tom}
                  onChange={(e) => setForm({ ...form, voz_tom: e.target.value })}
                >
                  {VOZ_TONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Nível de manchete (CTR)</span>
                <select
                  value={form.voz_clickbait}
                  onChange={(e) => setForm({ ...form, voz_clickbait: e.target.value })}
                >
                  {VOZ_CLICKBAIT.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Instrução extra da marca (opcional)</span>
                <textarea
                  value={form.voz_custom}
                  placeholder="ex.: sempre aproximar o fato do leitor da região Norte; citar o programa da casa quando a pauta vier dele"
                  onChange={(e) => setForm({ ...form, voz_custom: e.target.value })}
                  rows={2}
                />
              </label>
            </fieldset>

            <fieldset className={styles.fieldset}>
              <legend>Mapeamento do publisher (campos custom do WP)</legend>
              <p className={styles.muted}>
                Para cada variável do sistema, informe a <strong>chave de meta</strong> do
                WordPress de destino. Vazio = não envia. As chaves precisam estar registradas
                com <code>show_in_rest</code> no WP (caso contrário o WordPress ignora).
              </p>
              {PUBLISHER_VARS.map((v) => (
                <label key={v.key} className={styles.field}>
                  <span>{v.label}</span>
                  <input
                    value={form.field_map[v.key] ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        field_map: { ...form.field_map, [v.key]: e.target.value },
                      })
                    }
                    placeholder={v.placeholder}
                    autoComplete="off"
                  />
                </label>
              ))}
            </fieldset>

            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={closeModal}>
                Cancelar
              </Button>
              <Button
                type="submit"
                loading={createMut.isPending || updateMut.isPending}
              >
                {editing ? 'Guardar' : 'Criar'}
              </Button>
            </div>

            {msg && <p className={styles.flashIn}>{msg}</p>}
          </form>
        </div>
      )}
    </section>
  );
}
